import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader } from '@/components/Card';
import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { Input } from '@/components/Input';
import { Badge } from '@/components/Badge';
import { api, ApiError } from '@/lib/api';
import { useMe, type AuthUser } from '@/lib/auth';
import { useCreatePersonalToken, usePersonalTokens, useRevokePersonalToken } from '@/lib/personal-tokens';

interface MfaStatus {
  enabled: boolean;
  enabledAt: string | null;
  remainingRecoveryCodes: number;
}

export function AccountTab() {
  const me = useMe();
  const mfa = useQuery({
    queryKey: ['mfa-status'],
    queryFn: () => api.get<MfaStatus>('/api/mfa/status'),
  });

  if (!me.data) return <p className="text-body text-text-secondary">Loading…</p>;

  return (
    <div className="flex flex-col gap-gap-element-lg">
      <Card>
        <CardHeader title="Profile" description="Your name and email." />
        <ProfileForm user={me.data} />
      </Card>

      <Card>
        <CardHeader title="Password" description="Change the password used to sign in." />
        <ChangePasswordForm />
      </Card>

      <Card>
        <CardHeader
          title="Two-factor authentication"
          description="Required by HEXDI for all accounts. Use an authenticator app like 1Password or Google Authenticator."
          action={
            mfa.data ? (
              <Badge tone={mfa.data.enabled ? 'success' : 'warning'}>
                {mfa.data.enabled ? 'Enabled' : 'Not enabled'}
              </Badge>
            ) : null
          }
        />
        <MfaPanel status={mfa.data} />
      </Card>

      <Card>
        <CardHeader
          title="API tokens"
          description="Let Claude Code (the HEXDI MCP) or the catalog import tool act as you. A token has your permissions on the routes that accept tokens; it can never publish a catalog release or manage your account."
        />
        <ApiTokensPanel />
      </Card>
    </div>
  );
}

function ApiTokensPanel() {
  const tokens = usePersonalTokens();
  const create = useCreatePersonalToken();
  const revoke = useRevokePersonalToken();
  const [name, setName] = useState('');
  const [shown, setShown] = useState<string | null>(null);
  const active = (tokens.data ?? []).filter((t) => !t.revokedAt);

  return (
    <div className="flex flex-col gap-3">
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          create.mutate(name.trim(), {
            onSuccess: (r) => {
              setShown(r.token);
              setName('');
            },
          });
        }}
      >
        <Field label="Token name" id="token-name">
          <Input
            id="token-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Claude on my laptop"
            maxLength={80}
          />
        </Field>
        <Button size="sm" type="submit" disabled={create.isPending || !name.trim()}>
          Create token
        </Button>
      </form>
      {shown && (
        <div className="rounded-md border border-border bg-background p-3">
          <p className="text-body-sm text-text-primary">Copy it now. You won't see this again.</p>
          <code className="mt-1 block break-all text-body-sm">{shown}</code>
          <Button
            size="sm"
            variant="secondary"
            className="mt-2"
            type="button"
            onClick={() => void navigator.clipboard?.writeText(shown)}
          >
            Copy
          </Button>
        </div>
      )}
      {active.length > 0 && (
        <ul className="flex flex-col gap-2">
          {active.map((t) => (
            <li key={t.id} className="flex flex-wrap items-center gap-3 text-body-sm text-text-secondary">
              <span className="text-text-primary">{t.name}</span>
              <span>created {t.createdAt.slice(0, 10)}</span>
              <span>last used {t.lastUsedAt ? t.lastUsedAt.slice(0, 10) : 'never'}</span>
              <Button size="sm" variant="ghost" type="button" disabled={revoke.isPending} onClick={() => revoke.mutate(t.id)}>
                Revoke
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const profileSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Enter a valid email'),
});

type ProfileValues = z.infer<typeof profileSchema>;

function ProfileForm({ user }: { user: AuthUser }) {
  const qc = useQueryClient();
  const update = useMutation({
    mutationFn: (input: ProfileValues) => api.patch<AuthUser>('/api/Clients/me', input),
    onSuccess: (data) => {
      qc.setQueryData(['me'], data);
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<ProfileValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: { name: user.name ?? '', email: user.email },
  });

  return (
    <form
      onSubmit={handleSubmit((values) => update.mutate(values))}
      className="flex flex-col gap-gap-element"
    >
      <Field id="profile-name" label="Name" error={errors.name?.message}>
        <Input id="profile-name" {...register('name')} />
      </Field>
      <Field
        id="profile-email"
        label="Email"
        error={errors.email?.message}
        hint="Changing email requires re-verification."
      >
        <Input id="profile-email" type="email" {...register('email')} />
      </Field>

      {update.error ? (
        <div className="rounded-md border border-danger/40 bg-danger/5 px-4 py-3 text-body-sm text-danger">
          {update.error instanceof ApiError ? update.error.message : 'Save failed'}
        </div>
      ) : null}
      {update.isSuccess ? (
        <div className="rounded-md border border-success/40 bg-success/5 px-4 py-3 text-body-sm text-success">
          Saved.
        </div>
      ) : null}

      <div className="flex justify-end">
        <Button type="submit" disabled={update.isPending || !isDirty}>
          {update.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </div>
    </form>
  );
}

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Required'),
    newPassword: z.string().min(8, 'At least 8 characters'),
    confirm: z.string(),
  })
  .refine((v) => v.newPassword === v.confirm, {
    message: 'Passwords must match',
    path: ['confirm'],
  });

type PasswordValues = z.infer<typeof passwordSchema>;

function ChangePasswordForm() {
  const change = useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      api.post<void>('/api/Clients/me/password', input),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirm: '' },
  });

  return (
    <form
      onSubmit={handleSubmit((values) =>
        change.mutate(
          { currentPassword: values.currentPassword, newPassword: values.newPassword },
          { onSuccess: () => reset() },
        ),
      )}
      className="flex flex-col gap-gap-element"
    >
      <Field id="cur-pw" label="Current password" error={errors.currentPassword?.message}>
        <Input id="cur-pw" type="password" autoComplete="current-password" {...register('currentPassword')} />
      </Field>
      <Field id="new-pw" label="New password" error={errors.newPassword?.message}>
        <Input id="new-pw" type="password" autoComplete="new-password" {...register('newPassword')} />
      </Field>
      <Field id="confirm-pw" label="Confirm new password" error={errors.confirm?.message}>
        <Input id="confirm-pw" type="password" autoComplete="new-password" {...register('confirm')} />
      </Field>

      {change.error ? (
        <div className="rounded-md border border-danger/40 bg-danger/5 px-4 py-3 text-body-sm text-danger">
          {change.error instanceof ApiError ? change.error.message : 'Could not update'}
        </div>
      ) : null}
      {change.isSuccess ? (
        <div className="rounded-md border border-success/40 bg-success/5 px-4 py-3 text-body-sm text-success">
          Password updated. Other devices have been signed out.
        </div>
      ) : null}

      <div className="flex justify-end">
        <Button type="submit" disabled={change.isPending}>
          {change.isPending ? 'Updating…' : 'Update password'}
        </Button>
      </div>
    </form>
  );
}

function MfaPanel({ status }: { status?: MfaStatus }) {
  const qc = useQueryClient();
  const [stage, setStage] = useState<'idle' | 'enroll' | 'verify' | 'codes'>('idle');
  const [challenge, setChallenge] = useState<{
    secret: string;
    qrDataUrl: string;
  } | null>(null);
  const [codes, setCodes] = useState<string[]>([]);
  const [verifyCode, setVerifyCode] = useState('');

  const begin = useMutation({
    mutationFn: () => api.post<{ secret: string; qrDataUrl: string }>('/api/mfa/enroll'),
    onSuccess: (data) => {
      setChallenge(data);
      setStage('verify');
    },
  });

  const verify = useMutation({
    mutationFn: () =>
      api.post<{ recoveryCodes: string[] }>('/api/mfa/enroll/verify', {
        code: verifyCode,
      }),
    onSuccess: (data) => {
      setCodes(data.recoveryCodes);
      setStage('codes');
      qc.invalidateQueries({ queryKey: ['mfa-status'] });
    },
  });

  const disable = useMutation({
    mutationFn: () => api.delete<void>('/api/mfa'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mfa-status'] });
      setStage('idle');
      setChallenge(null);
      setCodes([]);
      setVerifyCode('');
    },
  });

  useEffect(() => {
    if (status?.enabled) setStage('idle');
  }, [status?.enabled]);

  if (!status) return <p className="text-body text-text-secondary">Loading…</p>;

  if (status.enabled && stage === 'idle') {
    return (
      <div className="flex flex-col gap-gap-element">
        <p className="text-body text-text-secondary">
          Two-factor authentication is enabled. You have{' '}
          <strong>{status.remainingRecoveryCodes}</strong> recovery codes remaining.
        </p>
        <Button
          variant="secondary"
          onClick={() => disable.mutate()}
          disabled={disable.isPending}
        >
          {disable.isPending ? 'Disabling…' : 'Disable two-factor'}
        </Button>
      </div>
    );
  }

  if (stage === 'verify' && challenge) {
    return (
      <div className="flex flex-col gap-gap-element">
        <p className="text-body text-text-secondary">
          Scan this QR code with your authenticator app, then enter the 6-digit
          code below.
        </p>
        <img
          src={challenge.qrDataUrl}
          alt="MFA QR code"
          className="h-40 w-40 self-start rounded border border-border bg-surface"
        />
        <p className="text-body-sm text-text-secondary">
          Or enter this secret manually:{' '}
          <code className="rounded bg-border/40 px-2 py-0.5 font-mono">
            {challenge.secret}
          </code>
        </p>
        <Field id="totp-code" label="Verification code">
          <Input
            id="totp-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={verifyCode}
            onChange={(e) => setVerifyCode(e.target.value)}
          />
        </Field>
        <div className="flex gap-3">
          <Button onClick={() => verify.mutate()} disabled={verify.isPending}>
            {verify.isPending ? 'Verifying…' : 'Verify and enable'}
          </Button>
          <Button variant="ghost" onClick={() => setStage('idle')}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  if (stage === 'codes') {
    return (
      <div className="flex flex-col gap-gap-element">
        <p className="text-body text-text-secondary">
          Save these recovery codes in a safe place. Each can be used once if you
          lose access to your authenticator app. <strong>You won't see them again.</strong>
        </p>
        <ul className="grid grid-cols-2 gap-2 rounded-md border border-border bg-background p-4 font-mono text-body-sm">
          {codes.map((code) => (
            <li key={code}>{code}</li>
          ))}
        </ul>
        <Button onClick={() => setStage('idle')}>I've saved my codes</Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-gap-element">
      <p className="text-body text-text-secondary">
        Add a second factor to your sign-in. You'll be asked for a 6-digit code
        from your authenticator app each time you log in.
      </p>
      <Button onClick={() => begin.mutate()} disabled={begin.isPending}>
        {begin.isPending ? 'Starting…' : 'Set up two-factor'}
      </Button>
    </div>
  );
}
